import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ButtonComponent} from "../button/button.component";
import {NgClass, NgForOf} from "@angular/common";
import {RouterLink} from "@angular/router";
import {TranslatePipe} from "@ngx-translate/core";

export type ImageTextCard = {
  icon: string;
  title: string;
  description: string;
};

@Component({
  selector: 'app-image-text-card-wrapper',
  imports: [
    ButtonComponent,
    NgForOf,
    RouterLink,
    TranslatePipe,
    NgClass
  ],
  templateUrl: './image-text-card-wrapper.component.html',
  styleUrl: './image-text-card-wrapper.component.scss'
})
export class ImageTextCardWrapperComponent implements OnInit, OnDestroy {
  @Input() services: ImageTextCard[] = [];
  @Input() headline: string = '';
  @Input() learnMore: string = '';
  @Input() learnMoreHref: string = '';
  @Input() buttonName?: string = '';
  @Input() buttonHref?: string = '';
  @Input() applyShadow?: boolean = true;
  @Input() backgroundColor?: string = undefined;
  @Input() enableCarouselIndicators?: boolean = false;

  // carousel config
  currentIndex = 0;
  visibleCards = 3;
  private autoPlayTimer?: number;
  private direction: 1 | -1 = 1; // 1 = right, -1 = left
  private mobileScreen = '(min-width: 768px)';
  isDesktop = false;

  ngOnInit(): void {
    this.isDesktop = window.matchMedia(this.mobileScreen).matches;
    this.enableCarouselIndicators = this.isDesktop && this.enableCarouselIndicators;

    if (this.enableCarouselIndicators) {
      this.startAutoPlay();
    }
  }

  ngOnDestroy(): void {
    this.stopAutoPlay();
  }

  startAutoPlay(): void {
    this.autoPlayTimer = window.setInterval(() => {
      this.autoMove();
    }, 5000);
  }

  stopAutoPlay(): void {
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer);
    }
  }

  private resetAutoPlay(): void {
    if (!this.enableCarouselIndicators || !this.isDesktop) {
      return;
    }

    this.stopAutoPlay();
    this.startAutoPlay();
  }

  private autoMove(): void {
    if (!this.isDesktop) {
      return;
    }

    const maxIndex = this.dotCount - 1;

    if (this.currentIndex >= maxIndex) {
      this.direction = -1;
    } else if (this.currentIndex <= 0) {
      this.direction = 1;
    }

    this.currentIndex += this.direction;
  }

  /** Services to render */
  get visibleServices(): ImageTextCard[] {
    if (!this.enableCarouselIndicators || !this.isDesktop) {
      return this.services;
    }

    return this.services.slice(
      this.currentIndex,
      this.currentIndex + this.visibleCards
    );
  }

  /** Whether "..." should be shown */
  get hasMoreItems(): boolean {
    return this.services.length > this.visibleCards;
  }

  get dotCount(): number {
    if (!this.enableCarouselIndicators || !this.isDesktop) {
      return 0;
    }

    return Math.max(this.services.length - this.visibleCards + 1, 1);
  }

  goToIndex(windowIndex: number): void {
    if (!this.isDesktop) {
      return;
    }

    this.currentIndex = windowIndex;
    this.resetAutoPlay();
  }

  get maxIndex(): number {
    return Math.max(this.services.length - this.visibleCards, 0);
  }

  next(): void {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
    }
  }

  /** Move to the next 3 items, loop if needed */
  nextBatch(): void {
    const nextIndex = this.currentIndex + this.visibleCards;

    if (nextIndex >= this.services.length) {
      this.currentIndex = 0; // loop back
    } else {
      this.currentIndex = nextIndex;
    }
  }
}
